import { useState } from 'react';
import type { FormEvent } from 'react';
import { getReceptionStore } from './data/store.js';
import { dayRangeToIso } from './logic.js';
import { buildReviewQueue, groupReview, type ReviewItem } from './reviewLogic.js';
import { FrDateInput } from './ui/FrDateInput.js';

// « À apprendre » — the learning review queue (guest chat v2, Increment C9).
// Surfaces exchanges the AI couldn't answer (escalated) or answered wrong
// (staff-flagged), grouped by conversation. One tap drafts a generalized,
// PII-stripped FR fact from the guest question + the human's actual answer
// (/api/chat/kb-suggest); staff edit and approve it into the dynamic tier
// (a chat_notices fact) — effective on the next guest message, no deploy.
// Never unsupervised: the human always edits/approves before anything saves.

const REASON_LABELS = {
  escalated: 'Sans réponse IA',
  flagged: 'Réponse signalée',
} as const;

function weekAgoIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Per-item teach editor state (one open at a time). */
interface Editor {
  anchorId: number;
  draft: string;
  loading: boolean;
  saving: boolean;
}

export function Review() {
  const store = getReceptionStore();
  const [dateFrom, setDateFrom] = useState(weekAgoIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState<Editor | null>(null);
  const [savedCount, setSavedCount] = useState(0);

  async function search(e: FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEditor(null);
    try {
      const { fromIso, toIso } = dayRangeToIso(dateFrom, dateTo);
      const data = await store.loadReviewData(fromIso, toIso);
      setItems(
        buildReviewQueue(
          data.conversations,
          data.messages,
          new Set(data.flaggedIds),
          new Set(data.resolvedIds),
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'chargement impossible');
      setItems(null);
    } finally {
      setLoading(false);
    }
  }

  /** Open the teach editor, prefilled with an AI draft when there is a human answer to draft from. */
  async function propose(item: ReviewItem): Promise<void> {
    setEditor({ anchorId: item.anchorId, draft: '', loading: item.staffAnswerFr !== null, saving: false });
    if (item.staffAnswerFr === null) return; // no answer yet — staff writes the fact from scratch
    try {
      const draft = await store.suggestFact(item.questionFr, item.staffAnswerFr);
      setEditor((cur) => (cur && cur.anchorId === item.anchorId ? { ...cur, draft, loading: false } : cur));
    } catch {
      // Draft is a convenience — on failure, let staff write it themselves.
      setEditor((cur) => (cur && cur.anchorId === item.anchorId ? { ...cur, loading: false } : cur));
    }
  }

  async function saveFact(item: ReviewItem): Promise<void> {
    if (!editor || editor.anchorId !== item.anchorId) return;
    const bodyFr = editor.draft.trim();
    if (!bodyFr) return;
    setEditor({ ...editor, saving: true });
    setError('');
    try {
      await store.saveNotice({ kind: 'fact', bodyFr, showBanner: false, startsAt: null, endsAt: null });
      await store.resolveReviewItem(item.anchorId);
      markResolved(item.anchorId, true);
      setEditor(null);
      setSavedCount((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'enregistrement impossible');
      setEditor({ ...editor, saving: false });
    }
  }

  async function setResolved(item: ReviewItem, resolved: boolean): Promise<void> {
    setError('');
    try {
      if (resolved) await store.resolveReviewItem(item.anchorId);
      else await store.unresolveReviewItem(item.anchorId);
      markResolved(item.anchorId, resolved);
      if (editor?.anchorId === item.anchorId) setEditor(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'action impossible');
    }
  }

  function markResolved(anchorId: number, resolved: boolean): void {
    setItems((prev) => prev?.map((i) => (i.anchorId === anchorId ? { ...i, resolved } : i)) ?? prev);
  }

  const visible = (items ?? []).filter((i) => showResolved || !i.resolved);
  const groups = groupReview(visible);
  const pendingCount = (items ?? []).filter((i) => !i.resolved).length;

  return (
    <div className="review">
      <header className="review-header">
        <div>
          <h2>Apprentissage</h2>
          <p className="muted">
            Questions sans réponse ou réponses signalées — ajoutez la bonne réponse à la base de
            l’assistant en un geste.
          </p>
        </div>
      </header>

      <form className="review-filters" onSubmit={(e) => void search(e)}>
        <label>
          Du
          <FrDateInput value={dateFrom} onChange={setDateFrom} />
        </label>
        <label>
          Au
          <FrDateInput value={dateTo} onChange={setDateTo} />
        </label>
        <button type="submit" disabled={loading}>
          Rechercher
        </button>
        <label className="review-show-resolved">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Afficher les éléments traités
        </label>
      </form>

      {error && <p className="review-error">{error}</p>}
      {savedCount > 0 && (
        <p className="review-saved">
          {savedCount} fiche{savedCount > 1 ? 's' : ''} ajoutée{savedCount > 1 ? 's' : ''} à la base.
        </p>
      )}

      {items === null && !loading && (
        <p className="muted">Choisissez une période puis lancez la recherche.</p>
      )}
      {items !== null && pendingCount === 0 && !showResolved && (
        <p className="muted">Rien à apprendre sur cette période — l’assistant a tout couvert. 🎉</p>
      )}

      {groups.map((group) => (
        <section key={group.conversationId} className="review-group">
          <h3>Chambre {group.room}</h3>
          {group.items.map((item) => (
            <article key={item.anchorId} className={`review-item ${item.resolved ? 'is-resolved' : ''}`}>
              <div className="review-item-head">
                <span className={`review-reason is-${item.reason}`}>{REASON_LABELS[item.reason]}</span>
                {item.resolved && <span className="review-done">traité</span>}
              </div>

              <p className="review-q">
                <span className="review-label">Question</span>
                {item.questionFr}
              </p>
              {item.aiReplyFr && (
                <p className="review-ai">
                  <span className="review-label">Assistant</span>
                  {item.aiReplyFr}
                </p>
              )}
              {item.staffAnswerFr ? (
                <p className="review-staff">
                  <span className="review-label">Réponse réception</span>
                  {item.staffAnswerFr}
                </p>
              ) : (
                <p className="muted review-no-answer">Aucune réponse de la réception dans le fil.</p>
              )}

              {editor?.anchorId === item.anchorId ? (
                <div className="review-editor">
                  <label>
                    Fiche à ajouter (français, sans donnée personnelle)
                    <textarea
                      value={editor.draft}
                      onChange={(e) => setEditor({ ...editor, draft: e.target.value })}
                      rows={3}
                      maxLength={1000}
                      placeholder={
                        editor.loading ? 'Rédaction de la proposition…' : 'Ex. : Le local à vélos est au sous-sol, code 1234.'
                      }
                      disabled={editor.loading}
                      autoFocus
                    />
                  </label>
                  <div className="review-editor-actions">
                    <button
                      disabled={editor.loading || editor.saving || editor.draft.trim() === ''}
                      onClick={() => void saveFact(item)}
                    >
                      Enregistrer dans la base
                    </button>
                    <button className="secondary" disabled={editor.saving} onClick={() => setEditor(null)}>
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="review-actions">
                  {item.resolved ? (
                    <button className="secondary" onClick={() => void setResolved(item, false)}>
                      Rétablir
                    </button>
                  ) : (
                    <>
                      <button onClick={() => void propose(item)}>Proposer une fiche</button>
                      <button className="secondary" onClick={() => void setResolved(item, true)}>
                        Ignorer
                      </button>
                    </>
                  )}
                </div>
              )}
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
